import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { normalizeDigits } from '@kia/shared';
import { Input } from './Field';

/**
 * A numeric field that DISPLAYS with thousands separators (۱۲۰٬۰۰۰٬۰۰۰ → shown as 120,000,000)
 * and emits the plain numeric value. Native <input type="number"> can't render grouping, so this
 * is a text input that groups on the way in and parses on the way out. Persian/Arabic digits are
 * accepted (normalized, R8); decimals are opt-in for rate/percent fields.
 */

function sanitize(input: string, allowDecimal: boolean): string {
  let s = normalizeDigits(input);
  if (!allowDecimal) {
    // Integer field: DROP any fractional part (everything from the first decimal separator on) rather than
    // stripping the '.' and MERGING the digits — that turned a pasted "1,250,000.00" into 125,000,000, a
    // silent 100× error. The Persian decimal separator «٫» counts too.
    const cut = s.search(/[.٫]/);
    if (cut >= 0) s = s.slice(0, cut);
    s = s.replace(/[^\d-]/g, '');
  } else {
    s = s.replace(/٫/g, '.').replace(/[^\d.-]/g, '');
  }
  s = s.replace(/(?!^)-/g, ''); // only a single leading minus
  if (allowDecimal) {
    const first = s.indexOf('.');
    if (first >= 0) s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, '');
  }
  return s;
}

/** Group the integer part of a sanitized numeric string in thousands. Preserves a decimal tail. */
function group(s: string): string {
  if (s === '' || s === '-') return s;
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  const dot = body.indexOf('.');
  const intPart = (dot >= 0 ? body.slice(0, dot) : body).replace(/^0+(?=\d)/, '');
  const dec = dot >= 0 ? body.slice(dot) : '';
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + (grouped === '' && dec ? '0' : grouped) + dec;
}

export function NumberInput({
  value,
  onValue,
  allowDecimal = false,
  min,
  max,
  ...rest
}: {
  value: number | string | null | undefined;
  onValue: (n: number) => void;
  allowDecimal?: boolean;
  /** Clamp the value into [min, max] ON BLUR (not mid-type). Guards the "35 for 0.35" trap: a fraction lever
   *  bounded [0, 1] snaps a fat-fingered whole number back into range instead of silently storing 3500%.
   *  `| undefined` is explicit so callers can pass `BOUNDS[key]?.[0]` under exactOptionalPropertyTypes. */
  min?: number | undefined;
  max?: number | undefined;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max'>) {
  const [display, setDisplay] = useState('');
  const editing = useRef(false);

  // Sync the display from an external value (hydration / reset) — but never fight the user
  // mid-edit (a half-typed "0." or a trailing separator must survive keystrokes).
  useEffect(() => {
    if (editing.current) return;
    setDisplay(value === '' || value == null ? '' : group(sanitize(String(value), allowDecimal)));
  }, [value, allowDecimal]);

  return (
    <Input
      {...rest}
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      value={display}
      onFocus={() => {
        editing.current = true;
      }}
      onBlur={() => {
        editing.current = false;
        // Clamp to [min, max] on blur — after the user has finished typing, never mid-keystroke.
        if (min == null && max == null) return;
        const n = Number(sanitize(display, allowDecimal));
        if (!Number.isFinite(n)) return;
        const c = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
        if (c !== n) {
          setDisplay(group(sanitize(String(c), allowDecimal)));
          onValue(c);
        }
      }}
      onChange={(e) => {
        const s = sanitize(e.target.value, allowDecimal);
        setDisplay(group(s));
        const n = s === '' || s === '-' || s === '.' ? NaN : Number(s);
        onValue(Number.isFinite(n) ? n : 0);
      }}
    />
  );
}
